import CustomButton from "@components/buttons/CustomButton";
import LabelRow from "@components/rowcomponents/LabelRow";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { primeServices } from "@services/apis/Prime";
import { useState } from "react";
import { useMediaQuery } from "react-responsive";
import ConstantFunctions from "src/utils/constants_functions";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

export interface OpsClearUserDetails {
  dob?: string | null;
  email?: string | null;
  country?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}

interface ConfirmOpsClearModalProps {
  open: boolean;
  instanceId?: string;
  market: "canada" | "europe";
  senderName?: string | null;
  senderEmail?: string | null;
  userDetails?: OpsClearUserDetails | null;
  userCountryImage?: string | null;
  onClose: () => void;
  onSuccess?: (instanceId: string) => void;
}

const constantFunctions = new ConstantFunctions();

const ConfirmOpsClearModal = ({
  open,
  instanceId,
  market,
  senderName,
  senderEmail,
  userDetails,
  userCountryImage,
  onClose,
  onSuccess,
}: ConfirmOpsClearModalProps) => {
  const { handleError, handleSuccess } = useSnackbarMessage();
  const [loading, setLoading] = useState(false);

  const handleClear = async () => {
    if (!instanceId) return;
    setLoading(true);
    try {
      const response =
        market === "canada"
          ? await primeServices.cadOpsClear({
              instanceId,
              opsCleared: 1,
            })
          : await primeServices.ivyOpsClear({
              instanceId,
              opsCleared: 1,
            });
      handleSuccess({
        message: response?.msg || "Instance cleared successfully",
      });
      onSuccess?.(instanceId);
      onClose();
    } catch (error: any) {
      handleError({
        message:
          error?.response?.data?.msg ||
          error?.message ||
          "Failed to clear instance",
      });
    } finally {
      setLoading(false);
    }
  };

  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  return (
    <SlidingPanel
      isOpen={open && !!instanceId}
      onClose={loading ? () => {} : onClose}
      onClickCross={loading ? () => {} : onClose}
      heading="Confirm Ops Clear"
      showConfirm={false}
      showCross={!loading}
      isMobile={isMobile}
    >
      <div className="p-4 flex flex-col gap-4">
        {market === "canada" && (
          <div className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-bg-base px-3 py-2">
            <p className="text-[12px] font-semibold text-text-navbar-active underline">
              Sender
            </p>
            <LabelRow
              label="Name"
              value={senderName || undefined}
              isCopy={!!senderName}
            />
            <LabelRow
              label="Email"
              value={senderEmail || undefined}
              isCopy={!!senderEmail}
            />
          </div>
        )}

        <div className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-bg-base px-3 py-2">
          <p className="text-[12px] font-semibold text-text-navbar-active underline">
            User Details
          </p>
          <LabelRow
            label="Full Name"
            value={
              userDetails?.firstName && userDetails?.lastName
                ? userDetails.firstName + " " + userDetails.lastName
                : "- -"
            }
            isCopy={!!userDetails?.firstName || !!userDetails?.lastName}
          />

          <LabelRow
            label="Email"
            value={userDetails?.email ?? "- -"}
            isCopy={!!userDetails?.email}
          />

          <LabelRow
            label="Date of Birth"
            value={
              userDetails?.dob
                ? constantFunctions.dateFormatInDDMMYY(userDetails.dob)
                : userDetails?.dob
                  ? constantFunctions.dateFormatInDDMMYY(userDetails.dob)
                  : "- -"
            }
          />

          <LabelRow
            label="Country"
            value={userDetails?.country ?? "- -"}
            valueComp={
              <div className="flex flex-row gap-[4px] items-center">
                {userCountryImage && (
                  <img
                    src={userCountryImage}
                    height={12}
                    width={12}
                    alt=""
                  />
                )}
                <span className="text-text-base text-[12px] font-normal">
                  {userDetails?.country ?? "- -"}
                </span>
              </div>
            }
          />
        </div>

        <p className="text-xs text-text-subtle">
          You are about to mark instance{" "}
          <strong className="text-text-base">{instanceId}</strong> as cleared.
          This will allow automated disbursements to proceed. Do you want to
          continue?
        </p>

        <div className="flex flex-col gap-2">
          <CustomButton
            text="CLEAR"
            isActive={true}
            isButtonLoading={loading}
            onClick={handleClear}
          />
          <CustomButton
            text="CANCEL"
            isActive={!loading}
            isHollow
            onClick={onClose}
          />
        </div>
      </div>
    </SlidingPanel>
  );
};

export default ConfirmOpsClearModal;
